export function escapeLegalText(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function safeId(value) {
  return String(value ?? "section")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "section";
}

export function termsSectionDomId(idPrefix, sectionId) {
  return `${safeId(idPrefix)}-${safeId(sectionId)}`;
}

export function safeTermsLinkHref(value) {
  const href = String(value ?? "").trim();
  if (href.startsWith("/") && !href.startsWith("//")) return href;
  try {
    const parsed = new URL(href);
    return ["https:", "mailto:"].includes(parsed.protocol) ? parsed.href : "";
  } catch {
    return "";
  }
}

function normalizedBlocks(section) {
  if (Array.isArray(section.blocks)) return section.blocks;
  if (Array.isArray(section.paragraphs)) return section.paragraphs.map((text) => ({ type: "paragraph", text }));
  return [];
}

export function assertTermsDocumentSchema(terms) {
  if (!terms || typeof terms !== "object") throw new TypeError("A Terms policy is required.");
  if (!Array.isArray(terms.sections) || !terms.sections.length) throw new TypeError("Terms sections are required.");
  const sectionIds = new Set();
  terms.sections.forEach((section, sectionIndex) => {
    const id = String(section?.id || "");
    if (!id || id !== safeId(id) || sectionIds.has(id)) throw new TypeError(`Terms section ${sectionIndex + 1} has an invalid or duplicate id.`);
    sectionIds.add(id);
    if (!String(section.title || "").trim()) throw new TypeError(`Terms section ${id} requires a title.`);
    const blocks = normalizedBlocks(section);
    if (!blocks.length) throw new TypeError(`Terms section ${id} requires content blocks.`);
    blocks.forEach((block, blockIndex) => {
      const location = `${id} block ${blockIndex + 1}`;
      if (!block || typeof block !== "object") throw new TypeError(`Terms ${location} is invalid.`);
      if (block.type === "paragraph") {
        if (!String(block.text || "").trim()) throw new TypeError(`Terms ${location} requires paragraph text.`);
        return;
      }
      if (block.type === "bullets") {
        if (!Array.isArray(block.items) || !block.items.length || block.items.some((item) => !String(item || "").trim())) {
          throw new TypeError(`Terms ${location} requires non-empty bullet items.`);
        }
        return;
      }
      if (block.type === "notice") {
        if (!String(block.title || "").trim() || !String(block.text || "").trim()) throw new TypeError(`Terms ${location} requires a notice title and text.`);
        if (!["warning", "review", "info"].includes(block.tone)) throw new TypeError(`Terms ${location} has an unsupported notice tone.`);
        return;
      }
      if (block.type === "links") {
        if (!Array.isArray(block.items) || !block.items.length) throw new TypeError(`Terms ${location} requires links.`);
        block.items.forEach((item) => {
          if (!String(item?.label || "").trim() || !safeTermsLinkHref(item?.href)) throw new TypeError(`Terms ${location} contains an invalid link.`);
        });
        return;
      }
      throw new TypeError(`Terms ${location} has unsupported type ${String(block.type)}.`);
    });
  });
  return true;
}

function termsBlockMarkup(block) {
  if (block.type === "paragraph") return `<p>${escapeLegalText(block.text)}</p>`;
  if (block.type === "bullets") {
    return `<ul class="terms-block-list">${block.items.map((item) => `<li>${escapeLegalText(item)}</li>`).join("")}</ul>`;
  }
  if (block.type === "notice") {
    return `<aside class="terms-block-notice terms-block-notice--${block.tone}" role="note"><strong>${escapeLegalText(block.title)}</strong><p>${escapeLegalText(block.text)}</p></aside>`;
  }
  const items = block.items.map((item) => {
    const href = safeTermsLinkHref(item.href);
    const external = href.startsWith("https:");
    return `<li><a href="${escapeLegalText(href)}"${external ? ' target="_blank" rel="noopener noreferrer external"' : ""}><strong>${escapeLegalText(item.label)}</strong>${item.description ? `<span>${escapeLegalText(item.description)}</span>` : ""}</a></li>`;
  }).join("");
  return `<ul class="terms-block-links">${items}</ul>`;
}

export function termsPolicyDescriptor(terms) {
  if (!terms || typeof terms !== "object") throw new TypeError("A Terms policy is required.");
  return Object.freeze({
    policyId: String(terms.policyId || ""),
    version: String(terms.version || ""),
    contentRevision: String(terms.contentRevision || ""),
    effectiveDate: String(terms.effectiveDate || ""),
    locale: String(terms.locale || "en-US"),
  });
}

export function isEffectiveTermsPolicy(terms) {
  const descriptor = termsPolicyDescriptor(terms);
  return Boolean(
    terms?.effective === true
    && descriptor.policyId
    && descriptor.version
    && descriptor.contentRevision
    && !descriptor.version.toLowerCase().startsWith("draft-")
    && descriptor.effectiveDate
    && descriptor.effectiveDate.toLowerCase() !== "not effective"
  );
}

export function termsDocumentSectionsMarkup(terms, { idPrefix = "terms-review", headingLevel = 3 } = {}) {
  assertTermsDocumentSchema(terms);
  const level = Math.min(6, Math.max(2, Number.parseInt(headingLevel, 10) || 3));
  return terms.sections.map((section, index) => {
    const id = termsSectionDomId(idPrefix, section.id);
    return `
      <section class="terms-section terms-dialog__section" id="${escapeLegalText(id)}">
        <header><span>${String(index + 1).padStart(2, "0")}</span><h${level}>${escapeLegalText(section.title)}</h${level}>${section.reviewRequired && !terms.effective ? "<b>LEGAL REVIEW</b>" : ""}</header>
        ${normalizedBlocks(section).map(termsBlockMarkup).join("")}
      </section>`;
  }).join("");
}
